import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { TabViewWrapper } from "@client/pages/CreateCampaign/CreateCampaign.styled";
import {
  Button,
  Flex,
  Spacer,
  TextField,
  Typography,
  useTheme,
} from "@ugc-ui/lego";
import { useStores } from "@client/store";
import { useIsMobile } from "@common/hooks/isMobile";
import { EditCampaignContainer } from "@client/pages/EditCampaign/EditCampaign.styled";
import { ConfirmActionModalType } from "@common/components/Modal/ConfirmActionModal/ConfirmActionModal.utils";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { getPreselectedDefaultValues } from "@common/components/CreateUpdateInputs/defaultValues";
import { joiResolver } from "@hookform/resolvers/joi";
import { schema } from "@common/components/CreateUpdateInputs/validation";
import { TechnicalNameInput } from "@common/components/CreateUpdateInputs/components/TechnicalNameInput";
import { CompanyNameInput } from "@common/components/CreateUpdateInputs/components/CompanyNameInput";
import { DescriptionInput } from "@common/components/CreateUpdateInputs/components/DescriptionInput";
import { LinkInput } from "@common/components/CreateUpdateInputs/components/LinkInput";
import { ViewLimitInput } from "@common/components/CreateUpdateInputs/components/ViewLimitInput";
import { EndDateInput } from "@common/components/CreateUpdateInputs/components/EndDateInput";
import { DeviceTypeInput } from "@common/components/CreateUpdateInputs/components/DeviceTypeInput";
import { CountryIsoInput } from "@common/components/CreateUpdateInputs/components/CountryIsoInput";
import { SexSelectInput } from "@common/components/CreateUpdateInputs/components/SexSelectInput";
import { AgeLimitSelect } from "@common/components/Select/AgeLimitSelect";
import { InterestsInput } from "@common/components/CreateUpdateInputs/components/InterestsInput";
import { UploadArea } from "@common/components/CreateUpdateInputs/components/UploadArea";
import { AntaresInfeedCampaignStatus } from "@common/typings/common";
import { getCompanyPage } from "@common/consts/Path";

export const EditCampaignTabView: React.FC = observer(() => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    client: {
      showCampaignDialogStore: {
        campaignData,
        saveCampaign: handleSaveCampaign,
        isSuccess,
        handleValidationError,
        isCampaignHadStartedOnce,
      },
      uploadAreaStore: {
        video,
        isVideoLoading,
        loadingProgress,
        handleUploadFile,
        detach: uploadAreaDetach,
      },
    },
    exitModalStore,
    interestStore,
    countryStore,
    videoPlayerModal,
    deviceTypeStore,
    ageLimitStore,
  } = useStores();
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    unregister,
  } = useForm({
    mode: "onSubmit",
    defaultValues: getPreselectedDefaultValues(campaignData),
    resolver: joiResolver(
      schema({ viewsLimit: campaignData.uniqueViewsCount })
    ),
  });

  useEffect(() => {
    if (isSuccess) {
      navigate(getCompanyPage(campaignData.uuid));
    }
  }, [navigate, isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    unregister("isMultiSubscription");
  }, [unregister]);

  const handleEndEdit = () => {
    exitModalStore.openDialogAndAttach({
      type: ConfirmActionModalType.CANCEL_EDIT,
      applyCallback: () => {
        navigate(getCompanyPage(campaignData.uuid));
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(handleSaveCampaign, handleValidationError)}>
      <TabViewWrapper>
        <Flex
          flexGrow={1}
          flexDirection="column"
          justifyContent="space-between"
        >
          <EditCampaignContainer theme={theme} flexDirection="column">
            <Flex flexDirection="column" gap={24}>
              <TechnicalNameInput control={control} errors={errors} />

              <CompanyNameInput control={control} errors={errors} />

              <DescriptionInput control={control} errors={errors} />

              <LinkInput control={control} errors={errors} />

              {[
                AntaresInfeedCampaignStatus.BLOCKED,
                AntaresInfeedCampaignStatus.PAUSED,
              ].includes(campaignData.status.id) && (
                <TextField
                  label="Количество текущих показов"
                  type="number"
                  disabled
                  value={campaignData.uniqueViewsCount.toString()}
                  dataCy="поле количество текущих показов"
                />
              )}

              <ViewLimitInput
                control={control}
                errors={errors}
                disabled={
                  isCampaignHadStartedOnce === undefined ||
                  isCampaignHadStartedOnce === true
                }
              />

              <EndDateInput control={control} errors={errors} />

              <Typography type="subtitle2" color={theme.gray.a100}>
                Таргетинг
              </Typography>

              <Flex flexDirection="row" justifyContent="space-between" gap={24}>
                <DeviceTypeInput
                  control={control}
                  errors={errors}
                  deviceTypeStore={deviceTypeStore}
                  disabled={
                    isCampaignHadStartedOnce === undefined ||
                    isCampaignHadStartedOnce === true
                  }
                />

                <CountryIsoInput
                  control={control}
                  errors={errors}
                  countryStore={countryStore}
                  disabled={
                    isCampaignHadStartedOnce === undefined ||
                    isCampaignHadStartedOnce === true
                  }
                />
              </Flex>

              <Flex gap={24}>
                <SexSelectInput
                  control={control}
                  errors={errors}
                  disabled={isCampaignHadStartedOnce}
                />

                <AgeLimitSelect
                  control={control}
                  errors={errors}
                  theme={theme}
                  ageLimitStore={ageLimitStore}
                  disabled={isCampaignHadStartedOnce}
                />
              </Flex>

              <InterestsInput
                control={control}
                errors={errors}
                interestStore={interestStore}
                preselected={campaignData.selectedInterests}
              />

              <Flex gap={16} style={{ marginTop: "20px" }}>
                <Button
                  theme={theme}
                  color="default"
                  variant="outlined"
                  onClick={handleEndEdit}
                  fullWidth
                  type="reset"
                >
                  Отмена
                </Button>
                <Button
                  color="primary"
                  theme={theme}
                  disabled={!isDirty}
                  fullWidth
                  type="submit"
                >
                  Сохранить
                </Button>
              </Flex>
            </Flex>
          </EditCampaignContainer>
        </Flex>

        <Flex flexDirection="column">
          {isMobile && (
            <>
              <Typography type="subtitle2" color={theme.gray.a40}>
                Черновик
              </Typography>
              <Spacer size={12} vertical />
            </>
          )}
          <UploadArea
            control={control}
            errors={errors}
            video={video}
            isVideoLoading={isVideoLoading}
            loadingProgress={loadingProgress}
            onUploadFile={handleUploadFile}
            uploadAreaDetach={uploadAreaDetach}
            campaignData={{
              name: watch("name"),
              description: watch("description"),
              link: watch("productLink"),
            }}
            exitModalStore={exitModalStore}
            videoPlayerModalStore={videoPlayerModal}
            currentAdvertiser={{
              legalEntityName: campaignData.legalEntityName,
              legalEntityNameLink: campaignData.legalEntityNameLink,
            }}
            deleteAvailable={!isCampaignHadStartedOnce}
          />
        </Flex>
      </TabViewWrapper>
    </form>
  );
});
